import {Combo, IComboState} from '@mintdata-wsp/common/components/combo/Combo';
import {MDIcon, MDIconSize} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {AnalyticsEventActions} from '@mintdata-wsp/common/controllers/analytics/AnalyticsEventActions';
import {Organization} from '@mintdata-wsp/common/state/DataState';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {Tooltip} from '@mintdata-wsp/mint-kit/impl/components/tooltip/Tooltip';
import {Dialogs} from '@mintdata-wsp/wsp/controllers/common/DialogsController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Column} from '@mintdata-wsp/wsp/ui/components/forms/Column';
import {Row} from '@mintdata-wsp/wsp/ui/components/forms/Row';
import {paths} from '@mintdata-wsp/wsp/ui/routes/Routing';
import {SettingsRoutes} from '@mintdata-wsp/wsp/ui/views/workspace/settings/SettingsPanel';
import {Category, mk} from '@mintdata/mint-kit/api/log/mk';
import {IListItemState, ListModel} from '@mintdata/mint-kit/api/models/List';
import {IContextMenuItem, PopupAnchor, SourceAnchor} from '@mintdata/mint-kit/impl/components/contextMenu/ContextMenu';
import {PopupHelpers} from '@mintdata/mint-kit/impl/components/popover/PopupHelpers';
import {PopupManager} from '@mintdata/mint-kit/impl/components/popover/PopupManager';
import {PopupItemWithIndex} from '@mintdata/mint-kit/impl/components/popover/PopupState';
import {PopupSize} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {IStyles} from '@mintdata/mint-kit/impl/util/Styles';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, computed} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./SidebarOrgSwitcher.less');
const cx = classnames.bind(styles);

interface ISidebarOrgSwitcherItem extends IContextMenuItem
{
    organization?:Organization;
}

@observer
export class SidebarOrgSwitcher extends React.Component<any, any>
{
    @computed
    private get _menuItems():Array<ISidebarOrgSwitcherItem>
    {
        return rootState.data.organizations.map((organization:Organization) =>
        {
            return {organization};
        });
    }

    private onClickSettings = (e:React.MouseEvent<HTMLElement>) =>
    {
        e.stopPropagation();

        controllers.ui.mainPage.setMobileMenuVisibility(false);

        controllers.navigation.navigateTo(`${paths.settings()}/${SettingsRoutes.Spaces}`);
    };

    @bind
    private _menuProvider():ListModel<ISidebarOrgSwitcherItem>
    {
        return new ListModel(this._menuItems.map((item:ISidebarOrgSwitcherItem) =>
        {
            return {
                ...item,
            } as ISidebarOrgSwitcherItem;
        }));
    }

    @action.bound
    private _selectMenuItem(item:ISidebarOrgSwitcherItem)
    {
        this._onSwitchOrganization(item.organization);
    }

    @bind
    private _onSwitchOrganization(organization:Organization)
    {
        mk.debug(Category.Navigation, `_onSWITCHOrganization()...`);

        controllers.analytics.main.trackEvent(
            AnalyticsEventActions.WSP_ORG_SWITCHED,
            {
                org_sid: organization.siteId,
                org_name: organization.name
            }
        );

        controllers.user.switchToOrganization(organization);
    }

    private _onClickBtnAdd = (e:React.MouseEvent<HTMLElement>) =>
    {
        const el:HTMLElement = e.currentTarget;
        const popupItem:PopupItemWithIndex = PopupHelpers.findPopupFromElement(el);

        popupItem && PopupManager.getInstance().closePopup(popupItem.popupItem.id);

        controllers.ui.dialogs.showDialog(Dialogs.AddOrganization, false);
    };

    @bind
    private _renderMenuItem({organization}:ISidebarOrgSwitcherItem,
                            {}:IListItemState,
                            {}:IStyles):JSX.Element
    {
        const {currentOrganization} = rootState.userSession;

        if( organization && organization.siteId === currentOrganization.siteId ) return null;

        return (
            <Row className={styles.orgItem}>
                <MDIcon
                    className={styles.orgItemIcon}
                    name={MDIcons.common.Business}
                    size={MDIconSize.medium}
                />
                <Column className={styles.orgItemText}>
                    <div className={styles.orgItemTitle}>
                        {organization.name}
                    </div>
                    <div className={styles.orgItemSpaces}>
                        {organization.numOfProjects} space{organization.numOfProjects !== 1 ? 's' : ''}
                    </div>
                </Column>
            </Row>
        );
    }

    private _renderOrgTitle = ({}:ISidebarOrgSwitcherItem,
                               {isOpened}:IComboState,
                               {}:IStyles):JSX.Element =>
    {
        const {currentOrganization} = rootState.userSession;

        return (
            <div className={cx(styles.orgTitleWrapper, {isOpened})}>
                <div className={styles.orgTitle}>
                    {currentOrganization.name}
                </div>
                <div className={cx(styles.orgIconWrapper, styles.iconOpen)}>
                    <MDIcon
                        className={styles.orgIcon}
                        name={MDIcons.common.ChevronRight}
                    />
                </div>
            </div>
        );
    };

    private _renderOrgSelector = ():JSX.Element =>
    {
        const listModel = this._menuProvider();

        return (
            <Combo
                className={styles.menuWrapper}
                dataSource={listModel}
                contentRenderer={this._renderOrgTitle}
                itemRenderer={this._renderMenuItem}
                onSelectItem={this._selectMenuItem}
                sourceAnchor={SourceAnchor.LeftBottom}
                popoverAnchor={PopupAnchor.LeftTop}
                popupSize={PopupSize.Original}
                theme={styles}
            >
                <footer>
                    <Row
                        className={styles.btnAdd}
                        onClick={this._onClickBtnAdd}
                    >
                        <MDIcon
                            className={styles.btnAddIcon}
                            name={MDIcons.common.Add}

                        />
                        Create new
                    </Row>
                </footer>
            </Combo>
        );
    };

    render():JSX.Element
    {
        return (
            <div className={styles.orgSwitcher}>

                {this._renderOrgSelector()}
                <Tooltip
                    tooltipClassName={cx('tooltip')}
                    showArrow={false}
                    closeOnTargetClick={true}
                    content={'Settings'}
                    popoverProps={{
                        className: cx('tooltipPopover'),
                        sourceAnchor: SourceAnchor.CenterBottom,
                        popupAnchor: PopupAnchor.CenterTop,
                        ignoreEsc: true
                    }}
                    hoverOpenDelay={300}
                >
                <div
                    className={cx(styles.orgIconWrapper, styles.iconSettings)}
                    onClick={this.onClickSettings}
                    title="Settings"
                >
                    <MDIcon
                        className={styles.orgIcon}
                        name={MDIcons.common.Settings}
                    />
                </div>
                </Tooltip>
            </div>
        );
    }
}
